import { Currencies } from "@/constants/currency";
import { IAccounting } from "./accounting";
import { ICustomFieldAnswer } from "./product-category";
import {
  IProductSlotState,
  IQuantifiedProductSlotState,
} from "./product-slot-state";
import {
  OrderSessionState,
  Currency as OrderCurrency,
  OrderType,
} from "@smeetz/order-common";
import { IAccountingEntry } from "./accounting-entry";
import { BookingStep } from "@/constants/booking";
import { VatCalculation } from "@smeetz/compliance-common";

export enum BookingState {
  Impossible = 0, // Not used
  PendingInfo, // Waiting for user info
  PendingPayment, // Waiting for payment
  Completed, // Finished
  Canceled, // Canceled
  To_Refund = 6, // Booking canceled and should be refunded
  Abandoned = 7, // Canceled after non finishing booking
}

export const BookingStateString = {
  [BookingState.Impossible]: "Impossible",
  [BookingState.PendingInfo]: "Pending Info",
  [BookingState.PendingPayment]: "Pending Payment",
  [BookingState.Completed]: "Completed",
  [BookingState.Canceled]: "Canceled",
  [BookingState.To_Refund]: "To refund",
  [BookingState.Abandoned]: "Abandoned",
};

export enum SyncStatus {
  Pending = 0,
  Synced = 1,
  Failed = 2,
  Ignored = 3, // SMEETZPOS error
  InProgress = 4,
}

export interface IProductSlotBooking {
  _id: string;
  id?: number;
  uuid: string | null;
  reference: string;
  state: BookingState;
  type?: number;
  languageId: number | null;
  sellerId: number | null;
  userId?: string | null;
  customerId?: number | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  currency: Currencies;
  insertDateTime: string;
  // goerToken: string | null;
  // organiserToken: string | null;
  updatedAt: string;
  message: string;
  channel?: string;
  // zipCode: string | null;
  // address: string | null;
  // overDateTime: string | null;
  // bookingReminderDate: string | null;
  // cancellationEmailSendStatus: number; // default to 0
  // phone: string | null;
  // termsAndConditions: string | null;
  // emailMarketingOrganiser: 0 | 1;
  // emailMarketingSmeetz: 0 | 1;
  // company: string | null;
  // languageExt: string | null;
  // internalNotes: string | null;
  // specialRequirements: string | null;

  pssList: IProductSlotState[];
  accountings: IAccounting[];
  accountingEntries: any[];
  cashfloatUuid: string;
  customFieldsAnswers?: ICustomFieldAnswer[];
  firstPosConfigId?: number;
  isToBePartiallySynched?: boolean;

  seatsIoHoldToken?: any;
  groupId: number;
  isSynchedManually?: boolean;

  orderSessions: BaseOrderSession[];
  tabName?: string;

  isSuspended?: boolean; // related to French certification, if true, a suspend event is sent to the EFR, if set to false, a resume event is sent
  step?: BookingStep; // Current step in the booking flow
}

export enum BookingType {
  Booking = 1,
  Invoice = 2,
}

// Idenitifies fields needed to search a give ticket in a booking
export interface IBookingTicketSearch {
  priceId: number;
  categoryId: number;
  timeslotId?: number;
}

export interface IReceiptInfo {
  groupedPSSList: IQuantifiedProductSlotState[];
  total: number;
  groupInfo: {
    name: string;
    address: string;
  };
  bookingRef: string;
  date: string;
  currency: string;
}

export interface IBookingCustomerInfo {
  id: number | null;
  bookingUuid: string;
  email: string;
  lastName: string;
  firstName: string;
}

export interface BaseOrderSession {
  id: string; //uuid
  orderId: string;
  reference: string; // Human readable easy to share reference
  bookingId?: number;
  bookingUuid: string;
  currency: OrderCurrency;
  state: OrderSessionState;
  completionDate?: string;
  groupId: number;
  sellerId?: number;
  sellerCogn?: string;
  channel: string;
  sessionStart: string; // (datetime in utc of when the user started this order). Useful for b2c, but other apps have the freedom to add it as they wish cause it’s not very relevant to all apps).
  sessionEnd?: string; // in utc of when the session ended.
  syncedAt?: string; // when the order was synced
  visiteDate?: string;
  // for knowing when the order is abandoned and blocked availability released
  expireAt?: string;
  counterId?: number; // id of the counter used for pos, kiosk
  seqNumber?: number; // sequential number of the order from counter.
  createdAt: string;
  updatedAt: string;

  //POS only
  hasOrderLines?: boolean; // to check if any order lines were created in an order session
  signatureInfoIT?: { govId: any; url: any; signature: any }; // order signature info (Italy)
  signatureInfoAT?: {
    qrCodeData: any;
    cashRegisterSerialNumber: any;
    signed: any;
    timeSignature: any;
    receiptNumber: any;
  }; // order signature info (Austria)
  signatureInfoDE?: {
    fiscalNumber?: any;
    signature?: any;
    sec?: any;
    message?: any;
    transactionId?: any;
    revision?: any;
    raw?: any;
    timestamp?: any;
  }; // order signature info (Germany)
  signatureInfoES?: {
    sec?: string;
    fiscalNumber?: string;
    timestamp?: string;
    signature?: string;
  }; // order signature info (Spain)

  signatureInfoFR?: {
    signature: any;
    fiscalNumber: any;
    sec: any;
    documentType: any;
    operationType: any;
  }; // order signature info (France)
  signatureInfoHR?: {
    fiscalNumber?: string; // FISCAL RAČUN e.g. "2/01/1"
    zki?: string; // ZKI — 32-char hex, always present
    jir?: string; // JIR — UUID from CIS (null when offline)
    pkp?: string; // PKP — substitutes JIR when CIS is offline
    qrCodeUrl?: string; // pre-built URL: https://porezna.gov.hr/rn?jir=...
    oib?: string; // organizer OIB from EFR response tag
    timestamp?: string; // DATUM from EFR response tag e.g. "01.08.2016 11:28:34"
  }; // order signature info (Croatia)
  signatureInfoBE?: {
    signature?: string;
    fiscalNumber?: string;
    sec?: string;
    fdmRef?: FdmRef;
    posFiscalTicketNo?: string;
    deviceId?: string;
    posId?: string;
    terminalId?: string;
    fdmVersion?: string;
    posDateTime?: string;
    originalTransactionFdmRef?: FdmRef; // cancellations and reprints only
    locationCbe?: string; // establishment unit number
    footer?: string;
    vat?: VatCalculation[]; // FDM-computed VAT breakdown, overrides the POS lines
  }; // order signature info (Belgium)
  orderType?: OrderType;
  receiptPrinted?: boolean; // if a receipt was printed in the order session, used to process reprint events

  // MOI Malta work
  receiptCounterValue?: string;
  saleStarted?: boolean; // to know if the sale has started (first item added to the order), used for DE compliance
}

/**
 * Reference the FDM returns for every signed event, printed on Belgian receipts.
 */
export interface FdmRef {
  fdmId: string;
  fdmDateTime: string;
  eventLabel: string;
  eventCounter: string;
  totalCounter: string;
}

export interface IDiffObject {
  booking: Omit<
    IProductSlotBooking,
    "accountings" | "pssList" | "orderSessions"
  >;
  pssUpdates: IProductSlotState[];
  accountingUpdates: IAccounting[];
  accountingEntriesUpdates: [] /*oldAccountingEntries*/;
  newPSS: IProductSlotState[];
  newAccountings: IAccounting[];
  newAccountingEntries: IAccountingEntry[];
  newBookingCustomFieldsAnswers: ICustomFieldAnswer[];
  updatedBookingCustomFieldsAnswers: ICustomFieldAnswer[];
  newPSSCustomFieldsAnswers: ICustomFieldAnswer[];
  updatedPSSCustomFieldsAnswers: ICustomFieldAnswer[];
  orderSessionUpdates: BaseOrderSession[];
  newOrderSessions: BaseOrderSession[];
}
